<template>
  <section class="container-fluid px-0 orders-section">
    <div class="px-0">
      <div class="panel-toolbar">
        <p class="panel-toolbar-meta">
          <i class="fas fa-box-open me-1 text-primary"></i>
          {{ totalOrders }} pedidos registrados
          <span
            class="text-muted fs-7 ms-1"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title="Los pedidos pueden tardar unos minutos en actualizarse."
          >
            <i class="fas fa-info-circle"></i>
          </span>
        </p>
        <div class="panel-toolbar-actions d-flex flex-wrap gap-2 align-items-center">
          <div class="btn-group btn-group-sm view-toggle" role="group" aria-label="Vista">
            <button
              type="button"
              class="btn"
              :class="viewMode === 'list' ? 'btn-primary' : 'btn-outline-light'"
              title="Lista"
              @click="viewMode = 'list'"
            >
              <i class="fas fa-list"></i>
            </button>
            <button
              type="button"
              class="btn"
              :class="viewMode === 'grid' ? 'btn-primary' : 'btn-outline-light'"
              title="Cuadrícula"
              @click="viewMode = 'grid'"
            >
              <i class="fas fa-th-large"></i>
            </button>
          </div>
          <button class="btn btn-outline-primary" @click="forceRefresh" :disabled="isLoading">
            <i class="fas fa-sync-alt me-2"></i>
            {{ isLoading ? 'Actualizando...' : 'Actualizar' }}
          </button>
          <button
            class="btn btn-outline-success"
            @click="exportExcel"
            :disabled="exportLoading"
            title="Exportar pedidos filtrados a Excel"
          >
            <span v-if="exportLoading" class="spinner-border spinner-border-sm me-2"></span>
            <i v-else class="fas fa-file-excel me-2"></i>
            Exportar Excel
          </button>
        </div>
      </div>

      <div class="mb-3 w-100 orders-tabs-wrap">
        <ul class="nav nav-tabs flex-nowrap orders-tabs">
          <li class="nav-item" v-for="filter in statusFilters" :key="filter.value">
            <a
              class="nav-link"
              :class="{ active: currentStatusFilter === filter.value }"
              href="#"
              @click.prevent="currentStatusFilter = filter.value"
            >
              <i :class="filter.icon" class="me-1"></i>
              {{ filter.label }}
              <span class="badge bg-secondary ms-1">{{ getCountByFilter(filter.value) }}</span>
            </a>
          </li>
        </ul>
      </div>

      <div class="mb-3">
        <label for="shipping-method-filter" class="form-label">Enviado por:</label>
        <select id="shipping-method-filter" v-model="shippingMethodFilter" class="form-select">
          <option value="">Todos</option>
          <option value="correo_argentino">Correo Argentino</option>
          <option value="arrange">Acordar envío</option>
        </select>
      </div>
      <div class="mb-4">
        <div class="input-group">
          <span class="input-group-text bg-white border-end-0 text-muted">
            <i class="fas fa-search"></i>
          </span>
          <input
            type="text"
            class="form-control border-start-0 ps-0 text-black"
            v-model="searchQuery"
            placeholder="Buscar por cliente, email o #pedido..."
            @input="debounceSearch"
          />
        </div>
      </div>

      <div v-if="isLoading" class="orders-loading">
        <div v-for="n in 6" :key="n" class="skeleton-loader">
          <div class="skeleton-text w-50"></div>
          <div class="skeleton-text w-75"></div>
        </div>
      </div>

      <div v-else-if="error" class="alert alert-danger" role="alert">
        <i class="fas fa-exclamation-triangle me-2"></i>
        {{ error }}
        <button @click="fetchOrders" class="btn btn-outline-danger btn-sm ms-3">
          <i class="fas fa-sync-alt me-1"></i>Reintentar
        </button>
      </div>

      <!-- Vista lista -->
      <div v-else-if="viewMode === 'list' && filteredOrders.length" class="orders-list">
        <div
          v-for="order in filteredOrders"
          :key="order.id"
          class="order-row"
          :class="{ 'order-row--archived': order.is_archived }"
        >
          <div class="order-row__top">
            <div class="order-row__main">
              <div class="order-row__id-line">
                <span class="order-row__id">#{{ order.id }}</span>
                <span class="status-pill" :class="`status-pill--${order.status}`">
                  <i :class="getStatusIcon(order.status)" class="me-1"></i>
                  {{ order.status_display || getStatusLabel(order.status) }}
                </span>
                <span v-if="order.is_archived" class="badge bg-secondary ms-1">Archivado</span>
              </div>
              <div class="order-row__customer">
                <i class="fas fa-user text-primary me-2"></i>
                <strong>{{ getOrderCustomerName(order) }}</strong>
                <span v-if="order.customer_phone" class="text-muted ms-2">{{ order.customer_phone }}</span>
              </div>
              <div class="text-muted small">{{ order.delivery_type === 'pickup' ? 'Acordar envío · sin cargo en este pedido' : 'Correo Argentino' }}</div>
              <div class="order-row__meta text-muted small">
                <span><i class="fas fa-box me-1"></i>{{ getOrderLineItems(order).length }} productos</span>
                <span class="mx-2">·</span>
                <span><i class="far fa-clock me-1"></i>{{ formatDate(order.created_at) }}</span>
              </div>
              <ul v-if="previewLineItems(order).length" class="order-row__lines">
                <li v-for="line in previewLineItems(order)" :key="line.id || line.product">
                  <span class="order-row__line-name">{{ line.product_name }}</span>
                  <span v-if="line.variant_display" class="order-row__line-variant">
                    {{ line.variant_display }}
                  </span>
                  <span class="order-row__line-qty">×{{ line.quantity }}</span>
                </li>
                <li v-if="extraLineCount(order) > 0" class="order-row__line-more">
                  +{{ extraLineCount(order) }} producto{{ extraLineCount(order) === 1 ? '' : 's' }}
                </li>
              </ul>
            </div>
            <div class="order-row__total">
              <span class="order-row__total-value">{{ formatPrice(order.total) }}</span>
            </div>
          </div>
          <div class="order-row__toolbar">
            <div class="order-row__contact">
              <a
                v-if="whatsAppHref(order)"
                :href="whatsAppHref(order)"
                target="_blank"
                rel="noopener noreferrer"
                class="contact-link"
                title="Abrir WhatsApp"
              >
                <i class="mdi mdi-whatsapp"></i>
              </a>
              <button
                v-if="order.customer_email"
                type="button"
                class="contact-link"
                :title="`Enviar email sobre el pedido #${order.id}`"
                :aria-label="`Enviar email a ${order.customer_email} sobre el pedido #${order.id}`"
                @click="contactModal?.openForOrder(order)"
              >
                <i class="fas fa-envelope"></i>
              </button>
            </div>
            <div class="order-row__btns">
              <router-link
                :to="{ name: 'AdminOrderDetails', params: { orderId: order.id } }"
                class="btn btn-sm btn-outline-light"
              >
                <i class="fas fa-eye me-1"></i>Detalle
              </router-link>
              <button class="btn btn-sm btn-primary" @click="openStatusModal(order)">
                <i class="fas fa-exchange-alt me-1"></i>Estado
              </button>
              <button
                v-if="canArchiveOrder(order)"
                class="btn btn-sm btn-outline-secondary"
                :title="order.is_archived ? 'Desarchivar' : 'Archivar'"
                :disabled="archivingId === order.id"
                @click="toggleArchive(order)"
              >
                <i :class="order.is_archived ? 'fas fa-inbox' : 'fas fa-archive'"></i>
              </button>
              <button class="btn btn-sm btn-outline-danger" @click="confirmDeleteOrder(order)">
                <i class="fas fa-trash-alt"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Vista cuadrícula -->
      <div v-else-if="viewMode === 'grid' && filteredOrders.length" class="orders-grid">
        <article
          v-for="order in filteredOrders"
          :key="order.id"
          class="order-card"
          :class="{ 'order-card--archived': order.is_archived }"
        >
          <header class="order-card__head">
            <div>
              <span class="order-card__id">#{{ order.id }}</span>
              <span class="status-pill ms-2" :class="`status-pill--${order.status}`">
                <i :class="getStatusIcon(order.status)" class="me-1"></i>
                {{ order.status_display || getStatusLabel(order.status) }}
              </span>
              <span v-if="order.is_archived" class="badge bg-secondary ms-1">Archivado</span>
            </div>
            <time class="order-card__date">{{ formatDate(order.created_at) }}</time>
          </header>

          <div class="order-card__body">
            <p class="text-muted small mb-1">{{ order.delivery_type === 'pickup' ? 'Acordar envío · sin cargo en este pedido' : 'Correo Argentino' }}</p>
            <p class="order-card__customer mb-1">
              <i class="fas fa-user text-primary me-2"></i>
              {{ getOrderCustomerName(order) }}
            </p>
            <p class="text-muted small mb-2">
              <i class="fas fa-box me-1"></i>
              {{ getOrderLineItems(order).length }} productos
            </p>
            <ul v-if="previewLineItems(order).length" class="order-card__lines">
              <li v-for="line in previewLineItems(order)" :key="line.id || line.product">
                <span class="order-card__line-name">{{ line.product_name }}</span>
                <span v-if="line.variant_display" class="order-card__line-variant">
                  {{ line.variant_display }}
                </span>
                <span class="order-card__line-qty">×{{ line.quantity }}</span>
              </li>
              <li v-if="extraLineCount(order) > 0" class="order-card__line-more">
                +{{ extraLineCount(order) }} producto{{ extraLineCount(order) === 1 ? '' : 's' }}
              </li>
            </ul>
            <div class="order-card__total">
              <span class="order-card__total-label">Total</span>
              <span class="order-card__total-value">{{ formatPrice(order.total) }}</span>
            </div>
          </div>

          <footer class="order-card__foot">
            <div class="order-row__contact mb-2">
              <a
                v-if="whatsAppHref(order)"
                :href="whatsAppHref(order)"
                target="_blank"
                rel="noopener noreferrer"
                class="contact-link"
                title="Abrir WhatsApp"
              >
                <i class="mdi mdi-whatsapp"></i>
              </a>
              <button
                v-if="order.customer_email"
                type="button"
                class="contact-link"
                :title="`Enviar email sobre el pedido #${order.id}`"
                :aria-label="`Enviar email a ${order.customer_email} sobre el pedido #${order.id}`"
                @click="contactModal?.openForOrder(order)"
              >
                <i class="fas fa-envelope"></i>
              </button>
            </div>
            <div class="order-card__actions d-flex flex-wrap gap-1">
              <router-link
                :to="{ name: 'AdminOrderDetails', params: { orderId: order.id } }"
                class="btn btn-sm btn-outline-light flex-grow-1"
              >
                <i class="fas fa-eye me-1"></i>Detalle
              </router-link>
              <button class="btn btn-sm btn-primary flex-grow-1" @click="openStatusModal(order)">
                <i class="fas fa-exchange-alt me-1"></i>Estado
              </button>
              <button
                v-if="canArchiveOrder(order)"
                class="btn btn-sm btn-outline-secondary"
                :disabled="archivingId === order.id"
                @click="toggleArchive(order)"
              >
                <i :class="order.is_archived ? 'fas fa-inbox' : 'fas fa-archive'"></i>
              </button>
              <button class="btn btn-sm btn-outline-danger" @click="confirmDeleteOrder(order)">
                <i class="fas fa-trash-alt"></i>
              </button>
            </div>
          </footer>
        </article>
      </div>

      <div v-else-if="!isLoading && filteredOrders.length === 0" class="text-center py-5 text-muted">
        <i class="fas fa-box-open fa-3x mb-3"></i>
        <h4>No se encontraron pedidos</h4>
        <p>Probá con otro filtro o búsqueda</p>
      </div>
    </div>
  </section>

  <CustomerContactModal ref="contactModal" />

  <!-- Cambio de estado -->
  <div class="modal fade" id="statusOrderModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content order-admin-modal">
        <div class="modal-header border-bottom border-secondary">
          <h5 class="modal-title">
            Estado del pedido #{{ statusOrder?.id }}
          </h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body" v-if="statusOrder">
          <p class="text-muted small mb-3">
            Estado actual:
            <span class="status-pill ms-1" :class="`status-pill--${statusOrder.status}`">
              {{ statusOrder.status_display || getStatusLabel(statusOrder.status) }}
            </span>
          </p>
          <label class="form-label">Nuevo estado</label>
          <p class="text-muted small mb-2">
            Pasá el cursor sobre cada opción para ver para qué sirve.
          </p>
          <div class="status-options">
            <button
              v-for="opt in statusOptionsForModal"
              :key="opt.value"
              type="button"
              class="status-option-btn"
              :class="{
                active: selectedNewStatus === opt.value,
                disabled: opt.disabled,
              }"
              :disabled="opt.disabled"
              :title="opt.description"
              data-bs-toggle="tooltip"
              data-bs-placement="right"
              @click="selectedNewStatus = opt.value"
            >
              <span class="status-option-btn__head">
                <i :class="getStatusIcon(opt.value)" class="me-2"></i>
                <span>{{ opt.label }}</span>
                <i
                  v-if="opt.description"
                  class="fas fa-info-circle status-option-btn__info ms-auto"
                  :title="opt.description"
                  @click.stop
                ></i>
              </span>
              <span v-if="opt.description" class="status-option-btn__desc">{{ opt.description }}</span>
            </button>
          </div>
          <div
            v-if="selectedNewStatus === 'shipped'"
            class="alert alert-shipped-notice small mt-3 mb-0"
            role="alert"
          >
            <i class="fas fa-truck me-2"></i>
            Al aceptar, el pedido se marcará como <strong>Enviado</strong>. Si ya tiene código de
            seguimiento, después podrás elegir si querés notificárselo al cliente.
          </div>
          <p class="text-muted small mt-3 mb-0">
            Solo se puede archivar cuando está <strong>completado</strong> o <strong>cancelado</strong>.
          </p>
          <p v-if="!statusOptionsForModal.some((o) => !o.disabled)" class="text-warning small mt-2 mb-0">
            No hay transiciones disponibles desde este estado.
          </p>
        </div>
        <div class="modal-footer border-top border-secondary status-modal-footer">
          <div class="status-modal-row">
            <button
              type="button"
              class="btn btn-outline-secondary"
              :disabled="archivingId === statusOrder?.id"
              @click="toggleArchiveFromModal"
            >
              <i :class="statusOrder?.is_archived ? 'fas fa-inbox' : 'fas fa-archive'" class="me-2"></i>
              {{ statusOrder?.is_archived ? 'Desarchivar' : 'Archivar' }}
            </button>
            <button
              type="button"
              class="btn btn-outline-danger"
              @click="confirmDeleteFromModal"
            >
              <i class="fas fa-trash-alt me-2"></i>
              Eliminar
            </button>
          </div>
          <div class="status-modal-row status-modal-row--primary">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="isUpdating || !selectedNewStatus || selectedNewStatus === statusOrder?.status"
              @click="saveStatusChange"
            >
              <span v-if="isUpdating"><i class="fas fa-spinner fa-spin me-2"></i>Guardando...</span>
              <span v-else><i class="fas fa-check me-2"></i>Aceptar</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Confirmación de notificación de tracking -->
  <div class="modal fade" id="trackingNotificationModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content order-admin-modal">
        <div class="modal-header border-bottom border-secondary">
<h5 class="modal-title">Notificación de envío</h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body" v-if="shipmentNotificationOrder">
          <p>
            Pedido <strong>#{{ shipmentNotificationOrder.id }}</strong>
          </p>
          <template v-if="shipmentNotificationOrder.delivery_type === 'pickup'">
            <p>Acordar envío. Podés avisarle al cliente que su pedido fue despachado según lo acordado.</p>
          </template>
          <template v-else>
            <label for="trackingInputList" class="form-label">Número de seguimiento</label>
            <input
              id="trackingInputList"
              v-model="trackingInputValue"
              type="text"
              class="form-control bg-dark text-white border-secondary"
              placeholder="Ej: 000500076393019A3G0C701"
              @keyup.enter="saveTrackingFromList"
            >
            <p class="mt-2 mb-0 text-muted small">
              {{ shipmentNotificationOrder.shipment?.tracking_number
                ? 'Podés corregir el código antes de reenviar el aviso. Guardá el cambio primero.'
                : 'Este pedido todavía no tiene código. Cargalo para notificarlo, o cerrá para dejarlo sin notificar.' }}
            </p>
          </template>
        </div>
        <div class="modal-footer border-top border-secondary">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Ahora no</button>
          <button
            v-if="shipmentNotificationOrder?.delivery_type !== 'pickup' && trackingInputValue.trim() !== (shipmentNotificationOrder?.shipment?.tracking_number || '')"
            type="button"
            class="btn btn-outline-primary"
            :disabled="isSavingTracking || !trackingInputValue.trim()"
            @click="saveTrackingFromList"
          >
            <span v-if="isSavingTracking"><i class="fas fa-spinner fa-spin me-2"></i>Guardando…</span>
            <span v-else><i class="fas fa-save me-2"></i>Guardar código</span>
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :title="trackingNotificationTitle(shipmentNotificationOrder?.shipment)"
            :disabled="isNotifyingTracking || isSavingTracking || (shipmentNotificationOrder?.delivery_type === 'pickup' ? shipmentNotificationOrder?.shipping_notification_sent : !shipmentNotificationOrder?.shipment?.tracking_number || trackingInputValue.trim() !== shipmentNotificationOrder.shipment.tracking_number)"
            @click="notifyTrackingFromList"
          >
            <span v-if="isNotifyingTracking"><i class="fas fa-spinner fa-spin me-2"></i>Enviando…</span>
            <span v-else>
              <i class="fas fa-envelope me-2"></i>
              {{ shipmentNotificationOrder?.delivery_type === 'pickup' ? 'Enviar aviso de envío' : (shipmentNotificationOrder?.shipment?.tracking_notification_sent ? 'Reenviar código' : 'Enviar código') }}
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Eliminar -->
  <div class="modal fade" id="deleteOrderModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content order-admin-modal">
        <div class="modal-header border-bottom border-secondary">
          <h5 class="modal-title">Confirmar eliminación</h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body" v-if="selectedOrder">
          <div class="alert alert-danger">
            <i class="fas fa-exclamation-triangle me-2"></i>
            Esta acción no se puede deshacer y eliminará el pedido completamente.
          </div>
          <p>
            ¿Eliminar permanentemente el pedido <strong>#{{ selectedOrder.id }}</strong>?
          </p>
          <div class="order-summary my-3">
            <div class="d-flex justify-content-between">
              <span>Cliente:</span>
              <strong>{{ getOrderCustomerName(selectedOrder) }}</strong>
            </div>
            <div class="d-flex justify-content-between">
              <span>Total:</span>
              <strong class="order-price">{{ formatPrice(selectedOrder.total) }}</strong>
            </div>
          </div>
        </div>
        <div class="modal-footer border-top border-secondary">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
          <button
            type="button"
            class="btn btn-danger"
            @click="deleteOrderConfirmed"
            :disabled="isDeleting"
          >
            <span v-if="isDeleting"><i class="fas fa-spinner fa-spin me-2"></i>Eliminando...</span>
            <span v-else><i class="fas fa-trash-alt me-2"></i>Eliminar</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, onUnmounted, watch } from 'vue'
import {
  getOrders,
  deleteOrder as deleteOrderAPI,
  updateOrderStatus,
  toggleArchiveOrder,
} from '@/services/orders'
import { notifyShipmentTracking, updateShipment } from '@/services/shipping'
import { getOrderLineItems, getOrderCustomerName, getWhatsAppLink } from '@/utils/orderDisplay'
import {
  ORDER_STATUS_LABELS,
  getStatusLabel,
  getStatusIcon,
  getStatusDescription,
  getAllowedNextStatuses,
  canArchiveOrder,
  matchesPendingFilter,
} from '@/utils/orderStatus'
import { useToast } from 'vue-toastification'
import apiClient from '@/services/apiClient'
import CustomerContactModal from '@/components/admin/CustomerContactModal.vue'

const toast = useToast()
const contactModal = ref(null)
const bootstrap = window.bootstrap

const orders = ref([])
const isLoading = ref(true)
const error = ref(null)
const searchQuery = ref('')
const shippingMethodFilter = ref('')
const selectedOrder = ref(null)
const statusOrder = ref(null)
const selectedNewStatus = ref('')
const isUpdating = ref(false)
const isNotifyingTracking = ref(false)
const shipmentNotificationOrder = ref(null)
const trackingInputValue = ref('')
const isSavingTracking = ref(false)
const isDeleting = ref(false)
const archivingId = ref(null)
const exportLoading = ref(false)
const viewMode = ref('list')
const currentStatusFilter = ref('pending')

const statusFilters = [
  { value: 'pending', label: 'Pendientes', icon: 'fas fa-clock' },
  { value: 'paid', label: 'Pagados', icon: 'fas fa-check-circle' },
  { value: 'shipped', label: 'Enviados', icon: 'fas fa-truck' },
  { value: 'completed', label: 'Completados', icon: 'fas fa-check' },
  { value: 'cancelled', label: 'Cancelados', icon: 'fas fa-times' },
  { value: 'archived', label: 'Archivados', icon: 'fas fa-archive' },
]

const totalOrders = computed(() => (Array.isArray(orders.value) ? orders.value.length : 0))

const PREVIEW_LINES = 2

const previewLineItems = (order) => getOrderLineItems(order).slice(0, PREVIEW_LINES)

const extraLineCount = (order) =>
  Math.max(0, getOrderLineItems(order).length - PREVIEW_LINES)

const filteredOrders = computed(() => {
  if (!Array.isArray(orders.value)) return []

  let result = [...orders.value]
  const f = currentStatusFilter.value

  if (f === 'archived') {
    result = result.filter((o) => o.is_archived)
  } else if (f === 'pending') {
    result = result.filter((o) => matchesPendingFilter(o.status) && !o.is_archived)
  } else if (f) {
    result = result.filter((o) => o.status === f && !o.is_archived)
  }

  if (shippingMethodFilter.value) {
    result = result.filter((o) => o.delivery_type === (shippingMethodFilter.value === 'arrange' ? 'pickup' : 'delivery'))
  }

  if (searchQuery.value) {
    const raw = searchQuery.value.trim().toLowerCase()
    // "123" y "#123" deben matchear el pedido 123
    const q = raw.replace(/^#/, '')
    result = result.filter(
      (order) =>
        (getOrderCustomerName(order) || '').toLowerCase().includes(q) ||
        (order.recipient_name || '').toLowerCase().includes(q) ||
        (order.customer_email || '').toLowerCase().includes(q) ||
        String(order.id).includes(q) ||
        `#${order.id}`.includes(raw)
    )
  }

  return result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
})

const getCountByFilter = (filter) => {
  if (!Array.isArray(orders.value)) return 0
  if (filter === 'archived') return orders.value.filter((o) => o.is_archived).length
  if (filter === 'pending') {
    return orders.value.filter((o) => matchesPendingFilter(o.status) && !o.is_archived).length
  }
  return orders.value.filter((o) => o.status === filter && !o.is_archived).length
}

const statusOptionsForModal = computed(() => {
  if (!statusOrder.value) return []
  const current = statusOrder.value.status
  const allowed = getAllowedNextStatuses(current)
  return Object.entries(ORDER_STATUS_LABELS)
    .filter(([value]) => value !== 'payment_pending')
    .map(([value, label]) => ({
      value,
      label,
      description: getStatusDescription(value),
      disabled: value === current || !allowed.includes(value),
    }))
})

const parseOrdersResponse = (response) => {
  if (response && response.results) return response.results
  if (Array.isArray(response)) return response
  return []
}

const fetchOrders = async (ignoreCache = false, silent = false) => {
  try {
    if (!silent) {
      isLoading.value = true
      error.value = null
    }
    const response = await getOrders(ignoreCache)
    orders.value = parseOrdersResponse(response)
  } catch (err) {
    error.value = err.message
    orders.value = []
  } finally {
    if (!silent) isLoading.value = false
  }
}

const debounceSearch = (() => {
  let timeout
  return () => {
    clearTimeout(timeout)
    timeout = setTimeout(() => {}, 300)
  }
})()

let statusModalTooltips = []

const disposeStatusTooltips = () => {
  statusModalTooltips.forEach((t) => t.dispose())
  statusModalTooltips = []
}

const initStatusTooltips = () => {
  disposeStatusTooltips()
  document
    .querySelectorAll('#statusOrderModal [data-bs-toggle="tooltip"]')
    .forEach((el) => {
      statusModalTooltips.push(new bootstrap.Tooltip(el, { trigger: 'hover' }))
    })
}

const onStatusModalShown = () => initStatusTooltips()
const onStatusModalHidden = () => disposeStatusTooltips()

const openStatusModal = (order) => {
  statusOrder.value = { ...order }
  selectedNewStatus.value = order.status || ''
  const el = document.getElementById('statusOrderModal')
  if (!el) return
  el.removeEventListener('shown.bs.modal', onStatusModalShown)
  el.removeEventListener('hidden.bs.modal', onStatusModalHidden)
  el.addEventListener('shown.bs.modal', onStatusModalShown)
  el.addEventListener('hidden.bs.modal', onStatusModalHidden)
  bootstrap.Modal.getOrCreateInstance(el).show()
}

const whatsAppHref = (order) => getWhatsAppLink(order?.customer_phone)
const saveStatusChange = async () => {
  if (!statusOrder.value || !selectedNewStatus.value) return
  try {
    isUpdating.value = true
    const newStatus = selectedNewStatus.value
    const updatedOrder = await updateOrderStatus(statusOrder.value.id, newStatus)
    const modalEl = document.getElementById('statusOrderModal')
    const modal = bootstrap.Modal.getInstance(modalEl)
    const hasShipment = newStatus === 'shipped' && (!!updatedOrder?.shipment || updatedOrder?.delivery_type === 'pickup')

    if (hasShipment && modalEl) {
      modalEl.addEventListener('hidden.bs.modal', () => {
        shipmentNotificationOrder.value = updatedOrder
        trackingInputValue.value = updatedOrder?.shipment?.tracking_number || ''
        const trackingEl = document.getElementById('trackingNotificationModal')
        bootstrap.Modal.getOrCreateInstance(trackingEl).show()
      }, { once: true })
    }

    modal?.hide()
    toast.success(`Pedido #${statusOrder.value.id} actualizado`)
    await fetchOrders(true, true)
  } catch (err) {
    toast.error(err.message || 'Error al actualizar el estado')
  } finally {
    isUpdating.value = false
  }
}

const notifyTrackingFromList = async () => {
  const target = shipmentNotificationOrder.value
  if (!target || (target.delivery_type !== 'pickup' && !target.shipment?.id)) return
  try {
    isNotifyingTracking.value = true
    const result = target.delivery_type === 'pickup'
      ? (await apiClient.post(`/orders/${target.id}/notify_shipping/`)).data
      : await notifyShipmentTracking(target.shipment.id)
    if (result.shipment) target.shipment = result.shipment
    toast.success(result.status || `Código de seguimiento enviado para el pedido #${target.id}`)
    bootstrap.Modal.getInstance(document.getElementById('trackingNotificationModal'))?.hide()
    await fetchOrders(true, true)
  } catch (err) {
    toast.error(err.response?.data?.error || err.message || 'Error al enviar el código de seguimiento')
  } finally {
    isNotifyingTracking.value = false
  }
}

const saveTrackingFromList = async () => {
  const target = shipmentNotificationOrder.value
  const code = trackingInputValue.value.trim()
  if (!target?.shipment?.id || !code) return
  isSavingTracking.value = true
  try {
    const updated = await updateShipment(target.shipment.id, { tracking_number: code })
    target.shipment = updated
    toast.success(updated.tracking_notification_sent
      ? `Código corregido para el pedido #${target.id}. Reenviá el aviso al cliente.`
      : `Código de seguimiento guardado para el pedido #${target.id}`)
  } catch (err) {
    toast.error(err.response?.data?.error || err.message || 'Error al guardar el código de seguimiento')
  } finally {
    isSavingTracking.value = false
  }
}

const trackingNotificationTitle = (shipment) => {
  if (!shipment?.tracking_notification_sent) return 'Enviar el código de seguimiento al cliente'
  if (!shipment.tracking_notification_sent_at) return 'El código ya fue enviado anteriormente'
  const sentAt = new Date(shipment.tracking_notification_sent_at).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
  return `Último envío: ${sentAt}`
}

const toggleArchive = async (order) => {
  try {
    archivingId.value = order.id
    const data = await toggleArchiveOrder(order.id)
    const idx = orders.value.findIndex((o) => o.id === order.id)
    if (idx !== -1) orders.value[idx].is_archived = data.is_archived
    toast.success(data.message || (data.is_archived ? 'Pedido archivado' : 'Pedido desarchivado'))
    return data
  } catch (err) {
    const fallback = 'No se pudo archivar el pedido'
    const base = err?.message || fallback
    const ruleHint = 'Solo se pueden archivar pedidos completados o cancelados.'
    const message = base.includes(ruleHint) ? base : `${base}. ${ruleHint}`
    toast.error(message)
    return null
  } finally {
    archivingId.value = null
  }
}

const toggleArchiveFromModal = async () => {
  if (!statusOrder.value) return
  const data = await toggleArchive(statusOrder.value)
  if (data) statusOrder.value.is_archived = data.is_archived
}

const confirmDeleteOrder = (order) => {
  selectedOrder.value = { ...order }
  const el = document.getElementById('deleteOrderModal')
  if (el) new bootstrap.Modal(el).show()
}

const confirmDeleteFromModal = () => {
  if (!statusOrder.value) return
  selectedOrder.value = { ...statusOrder.value }
  bootstrap.Modal.getInstance(document.getElementById('statusOrderModal'))?.hide()
  const el = document.getElementById('deleteOrderModal')
  if (el) new bootstrap.Modal(el).show()
}

const deleteOrderConfirmed = async () => {
  try {
    isDeleting.value = true
    await deleteOrderAPI(selectedOrder.value.id)
    bootstrap.Modal.getInstance(document.getElementById('deleteOrderModal'))?.hide()
    toast.success(`Pedido #${selectedOrder.value.id} eliminado`)
    await fetchOrders()
  } catch (err) {
    toast.error(err.message || 'Error al eliminar')
  } finally {
    isDeleting.value = false
  }
}

const formatDate = (date) =>
  new Date(date).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

const formatPrice = (price) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(price ?? 0)

const exportExcel = async () => {
  exportLoading.value = true
  try {
    const params = { shipping_method: shippingMethodFilter.value }
    if (currentStatusFilter.value === 'archived') {
      params.archived = 'true'
    } else if (currentStatusFilter.value === 'pending') {
      params.archived = 'false'
    } else if (currentStatusFilter.value) {
      params.status = currentStatusFilter.value
      params.archived = 'false'
    }
    const response = await apiClient.get('/orders/export/', { params, responseType: 'blob' })
    const url = URL.createObjectURL(response.data)
    const a = document.createElement('a')
    a.href = url
    a.download = `pedidos_${new Date().toISOString().split('T')[0]}.xlsx`
    a.click()
    URL.revokeObjectURL(url)
    toast.success('Exportación lista')
  } catch {
    toast.error('Error al exportar pedidos')
  } finally {
    exportLoading.value = false
  }
}

const forceRefresh = async () => {
  try {
    isLoading.value = true
    error.value = null
    const response = await getOrders(true)
    orders.value = parseOrdersResponse(response)
    toast.success('Pedidos actualizados')
  } catch (err) {
    error.value = err.message
    toast.error('Error al actualizar')
    orders.value = []
  } finally {
    isLoading.value = false
  }
}

watch(viewMode, (mode) => {
  localStorage.setItem('adminOrdersView', mode)
})

onMounted(() => {
  const saved = localStorage.getItem('adminOrdersView')
  if (saved === 'grid' || saved === 'list') viewMode.value = saved
  fetchOrders()
  const refreshInterval = setInterval(fetchOrders, 5 * 60 * 1000)
  onUnmounted(() => clearInterval(refreshInterval))
})
</script>

<style scoped>
.orders-section {
  min-height: auto;
}

.orders-tabs-wrap {
  overflow-x: auto;
}

.orders-tabs .nav-link {
  white-space: nowrap;
  font-size: 0.85rem;
}

.view-toggle .btn {
  min-width: 2.25rem;
}

.orders-loading {
  display: grid;
  gap: 1rem;
}

.orders-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
  gap: 1.1rem;
}

.order-card {
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  background: linear-gradient(160deg, rgba(255, 255, 255, 0.05), rgba(0, 0, 0, 0.15));
  padding: 1rem 1.1rem 0.95rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.order-card:hover {
  transform: translateY(-3px);
  border-color: rgba(255, 255, 255, 0.18);
  box-shadow: 0 12px 26px rgba(0, 0, 0, 0.3);
}

.order-card--archived {
  opacity: 0.72;
}

.order-card__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  padding-bottom: 0.55rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.order-card__id {
  font-family: monospace;
  font-size: 0.88rem;
  color: var(--white-color-2, #aaa);
}

.order-card__date {
  font-size: 0.75rem;
  color: var(--white-color-2, #999);
  white-space: nowrap;
}

.order-card__body {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.order-card__customer {
  font-weight: 600;
  color: var(--white-color, #f3f3f3);
}

.order-card__total,
.order-row__total {
  margin-top: auto;
}

.order-card__total-label,
.order-row__total-label {
  display: block;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--white-color-2, #999);
}

.order-card__total-value,
.order-row__total-value,
.order-price {
  color: var(--white-color, #fff);
  font-weight: 700;
  font-size: 1.1rem;
}

.order-card__lines {
  margin: 0 0 0.35rem;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.7);
}

.order-card__lines li {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  align-items: baseline;
}

.order-card__line-name {
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
}

.order-card__line-variant {
  font-style: italic;
  color: rgba(255, 255, 255, 0.55);
}

.order-card__line-qty {
  margin-left: auto;
  color: rgba(255, 255, 255, 0.6);
}

.order-card__line-more {
  color: rgba(255, 255, 255, 0.45);
}

.order-card__total {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding-top: 0.35rem;
  border-top: 1px dashed rgba(255, 255, 255, 0.1);
}

.order-card__foot {
  padding-top: 0.6rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.order-card__actions .btn {
  border-radius: 10px;
}

.order-card__actions .btn-outline-danger,
.order-card__actions .btn-outline-secondary {
  flex: 0 0 auto;
  min-width: 40px;
  padding-left: 0.65rem;
  padding-right: 0.65rem;
}

.orders-list {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.order-row {
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 1rem 1.25rem;
  border: 1px solid var(--panel-border, rgba(255, 255, 255, 0.12));
  border-radius: 10px;
  background: var(--panel-surface, #1e1e1e);
}

.order-row__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.25rem;
}

.order-row__toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.order-row__btns {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  align-items: center;
}

.order-row__contact {
  display: flex;
  gap: 0.4rem;
  align-items: center;
}

.contact-link {
  display: inline-flex;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.06);
  color: var(--white-color-2, #ccc);
  text-decoration: none;
  transition: color 0.15s, border-color 0.15s, background 0.15s;
}

.contact-link:hover {
  color: var(--white-color, #fff);
  border-color: rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.1);
}

.order-row--archived {
  opacity: 0.75;
}

.order-row__id-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 0.35rem;
}

.order-row__id {
  font-family: monospace;
  color: var(--white-color-2, #aaa);
}

.order-row__total {
  text-align: right;
  flex-shrink: 0;
}

.order-row__lines {
  margin: 0.5rem 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  color: rgba(255, 255, 255, 0.68);
  font-size: 0.78rem;
}

.order-row__lines li {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  align-items: baseline;
}

.order-row__line-name {
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
}

.order-row__line-variant {
  font-style: italic;
  color: rgba(255, 255, 255, 0.55);
}

.order-row__line-qty {
  margin-left: auto;
  color: rgba(255, 255, 255, 0.6);
}

.order-row__line-more {
  color: rgba(255, 255, 255, 0.45);
}

.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.status-pill--pending,
.status-pill--payment_pending {
  background: rgba(255, 193, 7, 0.2);
  color: #ffc107;
}

.status-pill--paid {
  background: rgba(13, 202, 240, 0.2);
  color: #0dcaf0;
}

.status-pill--shipped {
  background: rgba(108, 117, 125, 0.25);
  color: #adb5bd;
}

.status-pill--completed {
  background: rgba(var(--bs-primary-rgb, 13, 110, 253), 0.2);
  color: var(--primary-color, #0d6efd);
}

.status-pill--cancelled {
  background: rgba(220, 53, 69, 0.2);
  color: #f87171;
}

.status-options {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.status-option-btn {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  padding: 0.65rem 0.85rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.2);
  color: var(--white-color, #fff);
  text-align: left;
  transition: border-color 0.15s, background 0.15s;
}

.status-option-btn__head {
  display: flex;
  align-items: center;
  width: 100%;
  font-weight: 600;
}

.status-option-btn__desc {
  display: block;
  margin-top: 0.35rem;
  padding-left: 1.5rem;
  font-size: 0.78rem;
  font-weight: 400;
  color: var(--white-color-2, #aaa);
  line-height: 1.35;
}

.status-option-btn__info {
  font-size: 0.8rem;
  opacity: 0.55;
  cursor: help;
}

.alert-shipped-notice {
  background: rgba(13, 202, 240, 0.12);
  border: 1px solid rgba(13, 202, 240, 0.35);
  color: #b8e8f5;
}

.tracking-code {
  padding: 0.9rem 1rem;
  border: 1px solid rgba(13, 202, 240, 0.35);
  border-radius: 8px;
  background: rgba(13, 202, 240, 0.12);
  color: #b8e8f5;
  font-family: monospace;
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-align: center;
}

.status-option-btn:hover:not(:disabled) {
  border-color: var(--primary-color, #0d6efd);
}

.status-option-btn.active {
  border-color: var(--primary-color, #0d6efd);
  background: rgba(var(--bs-primary-rgb, 13, 110, 253), 0.18);
  color: var(--white-color, #fff);
}

.status-option-btn.active .status-option-btn__desc,
.status-option-btn.active .status-option-btn__info {
  color: rgba(255, 255, 255, 0.85);
  opacity: 1;
}

.status-option-btn.disabled,
.status-option-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.skeleton-loader {
  background: #1a1a1a;
  padding: 1.25rem;
  border-radius: 8px;
}

.skeleton-text {
  height: 16px;
  margin-bottom: 8px;
  background: linear-gradient(90deg, #333 25%, #404040 50%, #333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
}

@keyframes loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.nav-tabs {
  border-bottom-color: rgba(255, 255, 255, 0.1);
}

.nav-tabs .nav-link {
  color: var(--white-color-2);
  border: none;
  border-bottom: 2px solid transparent;
  background: transparent;
}

.nav-tabs .nav-link.active {
  color: var(--primary-color);
  border-bottom-color: var(--primary-color);
}

.order-summary {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  padding: 1rem;
}

.status-modal-footer {
  flex-direction: column;
  align-items: stretch;
  gap: 0.75rem;
}

.status-modal-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
}

.status-modal-row--primary .btn-primary {
  font-weight: 600;
}

@media (max-width: 768px) {
  .order-row__top {
    flex-direction: column;
  }
  .order-row__total {
    text-align: left;
  }
  .order-row__toolbar {
    flex-direction: column;
    align-items: stretch;
  }
  .order-row__btns {
    justify-content: flex-start;
  }
  .status-modal-row {
    grid-template-columns: 1fr;
  }
}
</style>

<style>
:deep(.order-admin-modal) {
  background: var(--grey-color, #2a2a2a) !important;
  color: var(--white-color, #fff);
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.modal-backdrop.show {
  opacity: 0.72 !important;
}
</style>
